const entry = (id, label, kind, tip) => Object.freeze({ id, label, kind, tip });
export const JOURNAL_ENTRIES = Object.freeze([
  entry('sweetPotato', '地瓜', 'crop', '种下地瓜，收获后既能布置花圃，也能做一份炭烤地瓜。'),
  entry('tomato', '番茄', 'crop', '红果成熟时再采摘；番茄能与南瓜一起煮成浓汤。'),
  entry('corn', '玉米', 'crop', '耐心照料玉米，金黄的果实适合烘烤或炖煮。'),
  entry('pineapple', '菠萝', 'crop', '远航带回的菠萝种子，能让家园多一份热带果香。'),
  entry('pumpkin', '南瓜', 'crop', '留一块田给慢慢长大的南瓜，丰收后试试新料理。'),
  entry('crab', '沙蟹', 'seafood', '低潮时观察浅滩，小沙蟹常在沙边活动。'),
  entry('reefFish', '珊瑚鱼', 'seafood', '寻找近岸可见的鱼群，等浮漂下沉后再提竿。'),
  entry('silverJack', '银鲹', 'seafood', '升级鱼竿和船只后，驶向更深的水域寻找银鲹。'),
  entry('lobster', '刺龙虾', 'seafood', '准备潜水装备与补给，再探索礁石间的龙虾。'),
  entry('pearlOyster', '珍珠贝', 'seafood', '深水珍珠贝需要更好的潜水装备与快艇。'),
]);
const project = (id, title, cost, reward, detail) => Object.freeze({ id, title,
  cost: Object.freeze(cost), reward, detail });
export const HOME_PROJECTS = Object.freeze([
  project('windchime', '海风铃', { wood: 2, shells: 4 }, 10, '收集海滩贝壳，让家园多一串随风轻响的风铃。'),
  project('garden', '丰收花圃', { wood: 4, sweetPotato: 1 }, 25, '留下一颗亲手收获的地瓜，把丰收的颜色种在家园。'),
  project('tidepool', '潮汐展台', { wood: 6, shells: 1, reefFish: 1, meal_bakedPotato_1: 1 }, 40,
    '首次登陆一座远岛后，带回鱼获与炭烤地瓜，布置自己的海岛展台。'),
]);
export const HOME_BOARD = Object.freeze({ x: -2.3, z: -2.4 });
export const HOME_BOARD_APPROACH = Object.freeze({ x: -1.8, z: -3.2 });
export const HOME_BOARD_OBSTACLE = Object.freeze({ ...HOME_BOARD, r: .75 });
const TITLES = ['初来海岸', '海风居所', '丰收家园', '潮汐乐园'];
const ISLAND_IDS = ['spring', 'grove', 'ruins', 'beacon'];
const JOURNAL_BY_ID = new Map(JOURNAL_ENTRIES.map(item => [item.id, item]));
const LABELS = { wood: '木材', shells: '贝壳', meal_bakedPotato_1: '炭烤地瓜（任意等级）',
  ...Object.fromEntries(JOURNAL_ENTRIES.map(item => [item.id, item.label])) };
const POTATO_MEALS = ['meal_bakedPotato_1', 'meal_bakedPotato_2', 'meal_bakedPotato_3'];
const record = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const quantity = value => Number.isSafeInteger(value) && value > 0 ? value : 0;
const day = value => quantity(value) || 1;
const add = (a, b) => Math.min(Number.MAX_SAFE_INTEGER, a + b);
const owned = (bag, id) => Object.hasOwn(record(bag), id) ? quantity(bag[id]) : 0;

// Persist the player's choice, never an inferred step or a scene object.
export function normalizePlayerGoal(value) {
  const raw = record(value);
  if (raw.kind === 'wish' && HOME_PROJECTS.some(item => item.id === raw.id)
    || raw.kind === 'journal' && JOURNAL_BY_ID.has(raw.id)) return { kind: raw.kind, id: raw.id };
  if (raw.kind === 'order' && typeof raw.id === 'string' && /^\d+-[12]$/.test(raw.id)
    && Number.isSafeInteger(Number(raw.id.split('-')[0])) && Number(raw.id.split('-')[0]) > 0)
    return { kind: 'order', id: raw.id };
  return null;
}

export function normalizeIslandLife(value, legacyState = null) {
  const raw = record(value), journalRaw = record(raw.journal), projectsRaw = record(raw.projects);
  const journal = {}, projects = {};
  for (const item of JOURNAL_ENTRIES) {
    const saved = record(journalRaw[item.id]), count = quantity(saved.count);
    if (count) journal[item.id] = { count, firstDay: day(saved.firstDay) };
  }
  // Existing journal saves rely on successful acquisition events. Only older
  // saves without a journal can use still-held finished goods as evidence.
  if (!Object.hasOwn(raw, 'journal') && legacyState) {
    for (const item of JOURNAL_ENTRIES) {
      const count = add(owned(legacyState.inventory, item.id), owned(legacyState.storage, item.id));
      if (count) journal[item.id] = { count, firstDay: day(legacyState.dayId) };
    }
  }
  for (const definition of HOME_PROJECTS) {
    const saved = record(projectsRaw[definition.id]), submitted = record(saved.contributed);
    const contributed = {};
    for (const [id, required] of Object.entries(definition.cost)) {
      const count = Math.min(required, quantity(submitted[id]));
      if (count) contributed[id] = count;
    }
    projects[definition.id] = { contributed, completed: saved.completed === true
      && Object.entries(definition.cost).every(([id, required]) => contributed[id] === required) };
  }
  return { journal, projects };
}

export function recordJournal(state, itemId, amount) {
  const definition = JOURNAL_BY_ID.get(itemId), count = quantity(amount);
  if (!state || typeof state !== 'object' || Array.isArray(state) || !definition || !count)
    return { ok: false, changed: false, newEntry: false, message: '' };
  // Do not migrate the just-added inventory here, which would count this event twice.
  const life = normalizeIslandLife(state.islandLife), previous = life.journal[itemId];
  const next = add(previous?.count || 0, count), changed = !previous || next !== previous.count;
  if (!changed) return { ok: true, changed: false, newEntry: false, message: '' };
  life.journal[itemId] = { count: next, firstDay: previous?.firstDay || day(state.dayId) };
  state.islandLife = life;
  return { ok: true, changed: true, newEntry: !previous,
    message: previous ? '' : `海岛手记新增 · ${definition.label}` };
}

function held(state, itemId) {
  if (itemId === 'wood' || itemId === 'shells') return quantity(state?.[itemId]);
  if (itemId === 'meal_bakedPotato_1') return POTATO_MEALS.reduce((sum, id) => add(sum, owned(state?.inventory, id)), 0);
  return owned(state?.inventory, itemId);
}

export function projectStatus(state, id) {
  const definition = HOME_PROJECTS.find(item => item.id === id);
  if (!definition) return null;
  const saved = normalizeIslandLife(state?.islandLife).projects[id];
  const requirements = Object.entries(definition.cost).map(([itemId, required]) => ({ itemId,
    label: LABELS[itemId], required, contributed: saved.contributed[itemId] || 0, held: held(state, itemId) }));
  const total = requirements.reduce((sum, item) => sum + item.required, 0);
  const submitted = requirements.reduce((sum, item) => sum + item.contributed, 0);
  const visited = Array.isArray(state?.voyage?.visited) && ISLAND_IDS.some(islandId => state.voyage.visited.includes(islandId));
  const locked = !saved.completed && id === 'tidepool' && !visited;
  return { id, title: definition.title, completed: saved.completed,
    available: !saved.completed && !locked && requirements.some(item => item.contributed < item.required && item.held > 0),
    locked, requirements, progress: submitted / total,
    detail: saved.completed ? `已布置在家园 · ${definition.reward} 金奖励已领取`
      : locked ? '先登船探索，首次登陆一座远岛后即可提交材料。'
        : `${definition.detail} 已提交 ${submitted}/${total} 份材料，可分次提交。` };
}

function deduct(state, itemId, amount) {
  if (itemId === 'wood' || itemId === 'shells') { state[itemId] -= amount; return; }
  const ids = itemId === 'meal_bakedPotato_1' ? POTATO_MEALS : [itemId];
  for (const id of ids) {
    const taken = Math.min(amount, owned(state.inventory, id));
    if (!taken) continue;
    state.inventory[id] -= taken; amount -= taken;
    if (!state.inventory[id]) delete state.inventory[id];
    if (!amount) break;
  }
}

export function contributeHomeProject(state, id, position) {
  const failed = message => ({ ok: false, changed: false, completed: false, message, reward: 0 });
  const status = projectStatus(state, id);
  if (!status) return failed('没有这份家园心愿。');
  if (state?.voyage?.mode !== 'home' || state?.shelter?.inside !== false)
    return failed('请先返航，在小屋外的家园心愿板提交材料。');
  const distance = Math.hypot(position?.x - HOME_BOARD.x, position?.z - HOME_BOARD.z);
  if (!Number.isFinite(position?.x) || !Number.isFinite(position?.z) || !Number.isFinite(distance) || distance > 2)
    return failed('先走近家园心愿板，再提交材料。');
  if (status.completed) return failed(`${status.title}已完成，奖励已经领取。`);
  if (status.locked) return failed(status.detail);
  if (!status.available) return failed('背包和随身材料中没有这份心愿缺少的物品；仓库物品需先取回。');
  const life = normalizeIslandLife(state.islandLife), saved = life.projects[id];
  for (const requirement of status.requirements) {
    const amount = Math.min(requirement.required - requirement.contributed, requirement.held);
    if (!amount) continue;
    deduct(state, requirement.itemId, amount);
    saved.contributed[requirement.itemId] = requirement.contributed + amount;
  }
  const completed = status.requirements.every(item => saved.contributed[item.itemId] === item.required);
  saved.completed = completed;
  const definition = HOME_PROJECTS.find(item => item.id === id), reward = completed ? definition.reward : 0;
  if (completed) state.gold = add(Number.isSafeInteger(state.gold) && state.gold >= 0 ? state.gold : 0, reward);
  state.islandLife = life;
  return { ok: true, changed: true, completed, reward,
    message: completed ? `${status.title}完成 · 家园焕然一新，获得 ${reward} 金`
      : `${status.title}材料已收下 · ${projectStatus(state, id).detail}` };
}

export function islandLifeSummary(state) {
  const life = normalizeIslandLife(state?.islandLife, state);
  const completed = HOME_PROJECTS.filter(item => life.projects[item.id].completed).length;
  return { level: completed + 1, title: TITLES[completed], completed, total: HOME_PROJECTS.length,
    journalCount: Object.keys(life.journal).length, journalTotal: JOURNAL_ENTRIES.length };
}
